import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface ApprovedUser {
  id: string;
  full_name: string | null;
  role: string;
  organization_id: string | null;
  org_name_requested: string | null;
  created_at: string;
  org_name?: string | null;
}

interface OrgOption {
  id: string;
  name_en: string;
  name_ro: string;
}

type RoleFilter = 'all' | 'partner' | 'manager';

interface EditState {
  userId: string;
  role: string;
  organization_id: string | null;
  full_name: string;
}

const ROLE_LABEL: Record<string, string> = {
  partner: 'Partner (NGO)',
  manager: 'Manager',
  admin: 'Administrator',
};

const ROLE_COLOR: Record<string, string> = {
  partner: 'bg-teal-50 text-teal-700 border-teal-100',
  manager: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  admin: 'bg-amber-50 text-amber-700 border-amber-100',
};

export default function UserManagement() {
  const [users, setUsers] = useState<ApprovedUser[]>([]);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [processing, setProcessing] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ApprovedUser | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [profilesRes, orgsRes] = await Promise.all([
      supabase
        .from('user_profiles')
        .select('id, full_name, role, organization_id, org_name_requested, created_at')
        .eq('is_approved', true)
        .in('role', ['partner', 'manager'])
        .order('created_at', { ascending: false }),
      supabase
        .from('organizations')
        .select('id, name_en, name_ro')
        .order('name_en'),
    ]);

    const orgList = (orgsRes.data ?? []) as OrgOption[];
    setOrgs(orgList);

    const orgMap = Object.fromEntries(orgList.map(o => [o.id, o.name_en]));
    const enriched: ApprovedUser[] = (profilesRes.data ?? []).map(u => ({
      ...u,
      org_name: u.organization_id ? orgMap[u.organization_id] ?? null : null,
    }));

    setUsers(enriched);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRevokeAccess = async () => {
    if (!confirmRevoke) return;
    setProcessing(confirmRevoke.id);
    const { error } = await supabase
      .from('user_profiles')
      .update({ is_approved: false })
      .eq('id', confirmRevoke.id);
    if (error) {
      showToast(`Error: ${error.message}`, 'error');
    } else {
      showToast('Access revoked successfully.');
      setUsers(prev => prev.filter(u => u.id !== confirmRevoke.id));
    }
    setProcessing(null);
    setConfirmRevoke(null);
  };

  const handleSaveEdit = async () => {
    if (!editState) return;
    setProcessing(editState.userId);
    const { error } = await supabase
      .from('user_profiles')
      .update({
        role: editState.role,
        organization_id: editState.organization_id || null,
        full_name: editState.full_name.trim() || null,
      })
      .eq('id', editState.userId);
    if (error) {
      showToast(`Error: ${error.message}`, 'error');
    } else {
      showToast('User updated successfully.');
      setEditState(null);
      fetchData();
    }
    setProcessing(null);
  };

  const openEdit = (user: ApprovedUser) => {
    setEditState({
      userId: user.id,
      role: user.role,
      organization_id: user.organization_id,
      full_name: user.full_name ?? '',
    });
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch =
      (u.full_name ?? '').toLowerCase().includes(q) ||
      (u.org_name ?? '').toLowerCase().includes(q) ||
      (u.org_name_requested ?? '').toLowerCase().includes(q);
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const stats = {
    total: users.length,
    partners: users.filter(u => u.role === 'partner').length,
    managers: users.filter(u => u.role === 'manager').length,
    linked: users.filter(u => u.organization_id).length,
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-6 z-[100] flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-close-circle-line'} />
          </span>
          {toast.msg}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: stats.total, icon: 'ri-team-line', color: 'text-navy' },
          { label: 'Partners', value: stats.partners, icon: 'ri-building-line', color: 'text-teal-600' },
          { label: 'Managers', value: stats.managers, icon: 'ri-user-settings-line', color: 'text-indigo-600' },
          { label: 'Linked to Org', value: stats.linked, icon: 'ri-links-line', color: 'text-emerald-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg border border-cool-gray-200 px-5 py-4">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-navy/40 font-medium uppercase tracking-wider">{s.label}</p>
              <span className={`w-6 h-6 flex items-center justify-center ${s.color}`}>
                <i className={`${s.icon} text-base`} />
              </span>
            </div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-lg border border-cool-gray-200">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-4 border-b border-cool-gray-200">
          <div className="flex-1">
            <h2 className="text-base font-bold text-navy">Approved Users</h2>
            <p className="text-xs text-navy/40 mt-0.5">{filtered.length} user{filtered.length !== 1 ? 's' : ''} shown</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Role filter */}
            <div className="flex items-center bg-cool-gray-100 rounded-lg p-0.5 text-xs">
              {(['all', 'partner', 'manager'] as RoleFilter[]).map(r => (
                <button
                  key={r}
                  onClick={() => setRoleFilter(r)}
                  className={`px-3 py-1.5 rounded-md font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${roleFilter === r ? 'bg-white text-navy shadow-sm' : 'text-navy/40 hover:text-navy/70'}`}
                >
                  {r === 'all' ? 'All' : r === 'partner' ? 'Partners' : 'Managers'}
                </button>
              ))}
            </div>
            {/* Search */}
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy/30 text-sm" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or org..."
                className="pl-8 pr-3 py-1.5 text-xs border border-cool-gray-200 rounded-lg focus:outline-none focus:border-navy/30 bg-white w-48"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy/50 border border-cool-gray-200 rounded-lg hover:bg-cool-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <i className="ri-loader-4-line text-2xl text-navy/30 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-cool-gray-100 rounded-full mx-auto mb-3">
              <i className="ri-user-line text-navy/30 text-xl" />
            </div>
            <p className="text-sm font-medium text-navy/50">No users found</p>
            <p className="text-xs text-navy/30 mt-1">
              {search || roleFilter !== 'all' ? 'Try adjusting your filters' : 'No approved users yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cool-gray-100">
                  <th className="text-left text-xs font-semibold text-navy/40 uppercase tracking-wider px-6 py-3">User</th>
                  <th className="text-left text-xs font-semibold text-navy/40 uppercase tracking-wider px-4 py-3">Role</th>
                  <th className="text-left text-xs font-semibold text-navy/40 uppercase tracking-wider px-4 py-3">Organization</th>
                  <th className="text-left text-xs font-semibold text-navy/40 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Joined</th>
                  <th className="text-right text-xs font-semibold text-navy/40 uppercase tracking-wider px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cool-gray-50">
                {filtered.map(user => (
                  <tr key={user.id} className="hover:bg-cool-gray-50/60 transition-colors">
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-navy/8 rounded-full flex-shrink-0">
                          <i className="ri-user-line text-navy/40 text-sm" />
                        </div>
                        <div>
                          <p className="font-semibold text-navy text-sm leading-tight">
                            {user.full_name || <span className="text-navy/30 italic">No name</span>}
                          </p>
                          <p className="text-xs text-navy/30 font-mono">{user.id.slice(0, 8)}…</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${ROLE_COLOR[user.role] ?? 'bg-stone-50 text-stone-600 border-stone-100'}`}>
                        {ROLE_LABEL[user.role] ?? user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      {user.org_name ? (
                        <span className="text-sm text-navy/70 font-medium">{user.org_name}</span>
                      ) : user.org_name_requested ? (
                        <span className="text-xs text-navy/40 italic flex items-center gap-1">
                          <i className="ri-link-unlink-m text-amber-400" />
                          {user.org_name_requested}
                          <span className="text-amber-500 ml-0.5">(not linked)</span>
                        </span>
                      ) : (
                        <span className="text-xs text-navy/20 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <span className="text-xs text-navy/40">
                        {new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => openEdit(user)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-navy border border-cool-gray-200 rounded-md hover:bg-cool-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-edit-line" />
                          Edit
                        </button>
                        <button
                          onClick={() => setConfirmRevoke(user)}
                          disabled={processing === user.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-500 border border-red-100 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-lock-line" />
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editState && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setEditState(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-navy">Edit User</h3>
              <button onClick={() => setEditState(null)} className="w-7 h-7 flex items-center justify-center text-navy/40 hover:text-navy cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-navy/50 uppercase tracking-wider mb-1.5">Full Name</label>
                <input
                  value={editState.full_name}
                  onChange={e => setEditState(prev => prev ? { ...prev, full_name: e.target.value } : null)}
                  className="w-full border border-cool-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-navy/30 focus:ring-1 focus:ring-navy/10"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-navy/50 uppercase tracking-wider mb-1.5">Role</label>
                <div className="flex gap-2">
                  {(['partner', 'manager'] as const).map(r => (
                    <button
                      key={r}
                      onClick={() => setEditState(prev => prev ? { ...prev, role: r } : null)}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-lg border transition-all cursor-pointer whitespace-nowrap ${editState.role === r ? 'bg-navy text-white border-navy' : 'border-cool-gray-200 text-navy/50 hover:border-navy/30'}`}
                    >
                      {r === 'partner' ? 'Partner (NGO)' : 'Manager'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-navy/50 uppercase tracking-wider mb-1.5">
                  Link to Organization
                </label>
                <select
                  value={editState.organization_id ?? ''}
                  onChange={e => setEditState(prev => prev ? { ...prev, organization_id: e.target.value || null } : null)}
                  className="w-full border border-cool-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-navy/30 bg-white"
                >
                  <option value="">— Not linked —</option>
                  {orgs.map(o => (
                    <option key={o.id} value={o.id}>{o.name_en}</option>
                  ))}
                </select>
                <p className="text-xs text-navy/30 mt-1">
                  <i className="ri-information-line mr-0.5" />
                  Partners linked to an org will see only their org in the portal
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditState(null)}
                className="flex-1 py-2.5 text-sm font-semibold text-navy/60 border border-cool-gray-200 rounded-lg hover:bg-cool-gray-50 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={processing === editState.userId}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-navy rounded-lg hover:bg-navy/90 disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {processing === editState.userId ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirm Modal */}
      {confirmRevoke && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setConfirmRevoke(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-7 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-lock-line text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-bold text-navy mb-2">Revoke Access?</h3>
            <p className="text-sm text-navy/50 leading-relaxed mb-5">
              <strong className="text-navy">{confirmRevoke.full_name || 'This user'}</strong> will lose access
              to the Partner Portal. You can re-approve them at any time from Partner Requests.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRevoke(null)}
                className="flex-1 py-2.5 text-sm font-semibold text-navy/60 border border-cool-gray-200 rounded-lg hover:bg-cool-gray-50 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeAccess}
                disabled={!!processing}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {processing ? 'Processing...' : 'Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
